import { Calendar, Constants, DateUtils, FeedItemSubType, FeedUtils, FileType, } from '..';
import { MarkupCommonUtils } from './MarkupCommonUtils';
export class SubmittalCommonUtils {
    static getSubmittalFeedDbName(projectId, submittalId) {
        return `p-${projectId}-submittal-${submittalId}-feed`;
    }
    static getSubmittalMarkupsDbName(projectId, submittalId) {
        return `p-${projectId}-submittal-${submittalId}-markups`;
    }
    static getLatestRevision(submittal) {
        if (!submittal.revisions || submittal.revisions.length === 0)
            return null;
        return submittal.revisions[submittal.revisions.length - 1];
    }
    static setLatestRevision(submittal, revision) {
        if (!submittal.revisions || submittal.revisions.length === 0) {
            submittal.revisions = [revision];
        }
        else {
            submittal.revisions[submittal.revisions.length - 1] = revision;
        }
        return submittal;
    }
    static isDraft(submittal) {
        const revision = this.getLatestRevision(submittal);
        return revision ? this.isDraftRevision(revision) : false;
    }
    static isDraftRevision(revision) {
        return revision.completed_state_index == undefined;
    }
    static isFinalState(revision) {
        return revision.completed_state_index == revision.states.length - 2;
    }
    static isComplete(submittal) {
        const revision = this.getLatestRevision(submittal);
        return revision ? this.isRevisionCompleted(revision) : false;
    }
    static isRevisionCompleted(revision) {
        return revision.completed_state_index == revision.states.length - 1;
    }
    static filterRevisionFeed(feed, revision) {
        return feed.filter((item) => item.revision === revision);
    }
    static filterRevisionMarkups(markups, revision) {
        return markups.filter((markup) => markup.revision === revision);
    }
    static filterRevisionFiles(files, feed) {
        const fileIds = feed
            .filter((item) => item.sub === FeedItemSubType.FILE && !item.deleted_date)
            .map((item) => item.sub_id);
        return files.filter((file) => fileIds.includes(file._id));
    }
    static filterAcceptedAnswers(revision, feed) {
        const byId = new Map(feed.map((item) => [item._id, item]));
        return (revision.accepted_answers || [])
            .map((id) => byId.get(id))
            .filter((item) => !!item);
    }
    static FILE_LIKE_FEED_SUBS = new Set([
        FeedItemSubType.FILE,
        FeedItemSubType.PHOTO,
        FeedItemSubType.DRAWING,
        FeedItemSubType.SPECIFICATION,
    ]);
    /** File ids for accepted file-like answers, in revision.accepted_answers order. */
    static getAcceptedFileIds(revision, feed) {
        const byId = new Map(feed.map((item) => [item._id, item]));
        const fileIds = [];
        const seen = new Set();
        for (const answerId of revision.accepted_answers || []) {
            const item = byId.get(answerId);
            if (!item?.sub_id || !this.FILE_LIKE_FEED_SUBS.has(item.sub) || seen.has(item.sub_id))
                continue;
            seen.add(item.sub_id);
            fileIds.push(item.sub_id);
        }
        return fileIds;
    }
    /** FileDocs for accepted file-like answers, in revision.accepted_answers order. */
    static getAcceptedFileDocs(revision, feed, files) {
        return this.getAcceptedFileIds(revision, feed)
            .map((id) => files.find((file) => file._id === id))
            .filter((file) => !!file && !file.deleted_by);
    }
    /** Resolve FileDocs for accepted file-like feed answers (document viewer / PDF cover). */
    static filterAcceptedFiles(acceptedAnswers, files) {
        const fileIds = Array.from(new Set(acceptedAnswers
            .filter((item) => this.FILE_LIKE_FEED_SUBS.has(item.sub) && item.sub_id)
            .map((item) => item.sub_id)));
        return fileIds
            .map((id) => files.find((file) => file._id === id))
            .filter((file) => !!file && !file.deleted_by);
    }
    static getCompletedState(revision) {
        if (revision.completed_state_index === undefined || !revision.states || revision.states.length === 0)
            return null;
        return revision.completed_state_index >= 0 && revision.completed_state_index < revision.states.length - 1
            ? revision.states[revision.completed_state_index]
            : null;
    }
    static getCurrentState(revision) {
        if (!revision.states || revision.states.length === 0)
            return null;
        if (revision.completed_state_index === undefined)
            return revision.states[0];
        return revision.completed_state_index >= 0 && revision.completed_state_index < revision.states.length - 1
            ? revision.states[revision.completed_state_index + 1]
            : null;
    }
    static getNextState(revision) {
        if (!revision.states || revision.states.length < 1)
            return null;
        if (revision.completed_state_index === undefined)
            return revision.states[1];
        return revision.completed_state_index >= 0 && revision.completed_state_index < revision.states.length - 2
            ? revision.states[revision.completed_state_index + 2]
            : null;
    }
    static getStateDueDate(state, previousDoneDate, calendar) {
        if (!state || !state.dueDays || state.dueDays <= 0)
            return previousDoneDate;
        const durationInSeconds = calendar.workingDaysToSeconds(state.dueDays);
        return calendar.getDateFromPositiveDuration(previousDoneDate, durationInSeconds);
    }
    /*
    static getRevisionCompletedStateId(revision: SubmittalRevision, feed: SubmittalFeedItemDoc[]): string | null {
      if (!revision.status_history || revision.status_history.length === 0) return null;
      const statusHistory = this.filterStatusFeedItems(feed, revision);
      const lastStateId =
        statusHistory && statusHistory.length > 0 ? statusHistory[statusHistory.length - 1].state_id : null;
      // Null state_id means the submittal has been moved back to the initial "created" state
      return lastStateId ?? null;
    }
  
    static getRevisionStateIndex(submittal: SubmittalDoc, revision: SubmittalRevision, feed: SubmittalFeedItemDoc[]): number {
      const statusHistory = this.filterStatusFeedItems(feed, revision);
      if (!statusHistory || statusHistory.length === 0) {
        return 0; // No history, at first state (created)
      }
  
      // Find the last non-null state_id in history
      let lastCompletedStateId = null;
      let consecutiveNulls = 0;
  
      for (let i = statusHistory.length - 1; i >= 0; i--) {
        if (statusHistory[i].state_id === null) {
          consecutiveNulls++;
        } else {
          lastCompletedStateId = statusHistory[i].state_id;
          break;
        }
      }
  
      // If all entries are null, we're at first state
      if (lastCompletedStateId === null) {
        return 0;
      }
  
      // Find where that completed state is in the workflow
      const completedStateIndex = submittal.states.findIndex((state) => state.id === lastCompletedStateId);
  
      // Current state = one after completed state, minus the number of backward steps (nulls)
      const currentStateIndex = completedStateIndex + 1 - consecutiveNulls;
  
      return Math.max(0, currentStateIndex); // Don't go below 0
    }
      */
    static filterStatusFeedItems(feed, revision) {
        return feed.filter((item) => item.sub === FeedItemSubType.STATUS && item.revision === revision.num);
    }
    static getPreviousStatusFeedItem(feed, submittal) {
        const revision = this.getLatestRevision(submittal);
        if (!revision)
            return null;
        return this.filterStatusFeedItems(feed, revision).sort(FeedUtils.sortNewestFirst).at(0) || null;
    }
    /** Same width as the file-pages viewer / markup coordinate space. */
    static PAGE_WIDTH = MarkupCommonUtils.MARKUP_PAGE_WIDTH;
    /** US Letter aspect ratio (11/8.5) at PAGE_WIDTH. */
    static PAGE_HEIGHT = Math.round(MarkupCommonUtils.MARKUP_PAGE_WIDTH * (11 / 8.5));
    static MARGIN = 48;
    static COLUMN_GAP = 24;
    static FONT = 'Arial, Helvetica, sans-serif';
    static BODY_SIZE = 11;
    static TITLE_SIZE = 22;
    static LINE_HEIGHT = 20;
    static LABEL_WIDTH = 148;
    static SECTION_GAP = 14;
    static TABLE_HEADER_HEIGHT = 24;
    static TABLE_ROW_HEIGHT = 24;
    static FEED_META_SIZE = 8.5;
    static FEED_TEXT_SIZE = 12;
    static FEED_ROW_MIN_HEIGHT = 56;
    static ATTACHMENT_ROW_HEIGHT = 52;
    static ATTACHMENT_THUMB_SIZE = 36;
    static MAX_ATTACHMENT_ROWS = 6;
    static get columnWidth() {
        return Math.floor((this.PAGE_WIDTH - this.MARGIN * 2 - this.COLUMN_GAP) / 2);
    }
    static get rightColumnX() {
        return this.MARGIN + this.columnWidth + this.COLUMN_GAP;
    }
    static getCoverPageSvg(options) {
        const { submittal, people, project, company, feed = [], files = [] } = options;
        const revision = this.getLatestRevision(options.submittal);
        if (!revision) {
            throw new Error('Revision not found');
        }
        const currentState = this.getCurrentState(revision);
        const leftFields = this.getLeftColumnFields(submittal, revision, currentState, people, options);
        const rightFields = this.getRightColumnFields(submittal, revision, people, project, feed);
        const fullWidthFields = this.getFullWidthFields(submittal, revision, currentState, people);
        const leftColumnSvg = this.renderFieldColumn(leftFields, this.MARGIN, 248, this.columnWidth);
        const rightColumnSvg = this.renderFieldColumn(rightFields, this.rightColumnX, 248, this.columnWidth);
        const fullWidthSvg = this.renderFieldColumn(fullWidthFields, this.MARGIN, Math.max(leftColumnSvg.endY, rightColumnSvg.endY) + 8, this.PAGE_WIDTH - this.MARGIN * 2);
        const attachmentsSection = this.renderAttachmentsSection(options.files || [], this.MARGIN, fullWidthSvg.endY + this.SECTION_GAP + 8, this.PAGE_WIDTH - this.MARGIN * 2);
        const workflowSection = this.renderWorkflowSection(submittal, revision, people, feed, this.MARGIN, attachmentsSection.endY + this.SECTION_GAP + 8, this.PAGE_WIDTH - this.MARGIN * 2, options.calendar ?? Calendar.getDefaultCalendar());
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${this.PAGE_WIDTH}" height="${this.PAGE_HEIGHT}" viewBox="0 0 ${this.PAGE_WIDTH} ${this.PAGE_HEIGHT}" style="display:block">
  <rect width="100%" height="100%" fill="#ffffff"/>
  <rect x="1" y="1" width="${this.PAGE_WIDTH - 2}" height="${this.PAGE_HEIGHT - 2}" fill="none" stroke="#222222" stroke-width="1"/>
  ${this.renderHeader(company, project)}
  ${this.renderTitle(submittal)}
  ${leftColumnSvg.svg}
  ${rightColumnSvg.svg}
  ${fullWidthSvg.svg}
  ${attachmentsSection.svg}
  ${workflowSection.svg}
  ${this.renderFooter(company, 1, 2)}
</svg>`;
    }
    static getLeftColumnFields(submittal, revision, currentState, people, options) {
        const createdDate = DateUtils.fromISO(revision?.created_date || submittal.created_date);
        const createdBy = revision?.created_by || submittal.created_by;
        const creator = createdBy ? this.findPerson(people, createdBy) : null;
        const responsibleContractor = creator?.company || creator?.first_name + ' ' + creator?.last_name;
        return [
            { label: 'Revision', value: String(revision?.num ?? 0) },
            { label: 'Status', value: this.getStatusLabel(currentState) },
            { label: 'Issue Date', value: createdDate ? DateUtils.format(createdDate) : '' },
            { label: 'Responsible Contractor', value: responsibleContractor },
            { label: 'Due Date', value: submittal.due_date ? DateUtils.formatIso(submittal.due_date) : '' },
        ];
    }
    static getRightColumnFields(submittal, revision, people, project, feed) {
        const specSection = submittal.spec_section;
        const costCode = submittal.cost_code;
        const createdDate = revision?.created_date || submittal.created_date;
        const statusHistory = feed.filter((item) => item.sub === FeedItemSubType.STATUS);
        const lastSubmitItem = statusHistory.find((item) => item.sub_id === Constants.SUBMITTAL_SUBMIT_STATUS_ID);
        return [
            {
                label: 'Submittal Manager',
                value: project?.submittals_manager ? this.formatPersonWithCompany(people, project.submittals_manager) : '',
            },
            { label: 'Date Created', value: createdDate ? DateUtils.formatIso(createdDate) : '' },
            {
                label: 'Spec Section',
                value: specSection ? [specSection.num, specSection.title].filter(Boolean).join(' - ') : '',
            },
            {
                label: 'Submit By',
                value: lastSubmitItem?.user_id
                    ? this.formatPersonWithCompany(people, lastSubmitItem?.user_id)
                    : 'Not Submitted',
            },
            {
                label: 'Cost Code',
                value: costCode ? [costCode.code_number, costCode.description].filter(Boolean).join(' - ') : '',
            },
            { label: 'Type', value: submittal.type ?? '' },
        ];
    }
    static getFullWidthFields(submittal, revision, currentState, people) {
        const lastState = revision ? revision.states?.[revision.states.length - 1] : null;
        return [
            { label: 'Submittal Package', value: submittal.package?.title ?? '' },
            { label: 'Approvers', value: this.formatPeopleList(people, lastState?.dueBy) },
            { label: 'Ball in Court', value: this.formatPeopleList(people, currentState?.dueBy) },
            { label: 'Distribution', value: this.formatPeopleList(people, submittal.distribution) },
            { label: 'Description', value: submittal.description ?? '', multiline: true },
        ];
    }
    static getStatusLabel(currentState) {
        return currentState?.action || 'Open';
    }
    static getCompanyAddressLines(company) {
        return [company.address, `${company.city}, ${company.state_code} ${company.zip}`.trim()].filter(Boolean);
    }
    static renderHeader(company, project) {
        const logo = this.renderLogo(company.logo, this.MARGIN, this.MARGIN, 200, 50);
        const companyTextX = this.MARGIN + 220;
        const companyLines = [
            company.name,
            ...this.getCompanyAddressLines(company),
            company.phone ? `P: ${company.phone}` : '',
        ]
            .filter(Boolean)
            .map((line, index) => `<tspan x="${companyTextX}" dy="${index === 0 ? 0 : 16}">${this.escapeXml(line)}</tspan>`)
            .join('');
        const projectLines = project
            ? [
                `Project: ${project.job_number ? `${project.job_number} ` : ''}${project.name}`,
                project.address,
                `${project.city}, ${project.state_code} ${project.zip}`.trim(),
            ].filter(Boolean)
            : [];
        const projectText = projectLines
            .map((line, index) => `<tspan x="${this.PAGE_WIDTH - this.MARGIN}" dy="${index === 0 ? 0 : 16}">${this.escapeXml(line)}</tspan>`)
            .join('');
        return `${logo}
  <text x="${companyTextX}" y="${this.MARGIN}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">${companyLines}</text>
  <text x="${this.PAGE_WIDTH - this.MARGIN}" y="${this.MARGIN}" text-anchor="end" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">${projectText}</text>
  <line x1="${this.MARGIN}" y1="${this.MARGIN + 70}" x2="${this.PAGE_WIDTH - this.MARGIN}" y2="${this.MARGIN + 70}" stroke="#cccccc" stroke-width="1"/>`;
    }
    static renderLogo(logo, x, y, maxWidth, maxHeight) {
        if (!logo || !logo.data) {
            return '';
        }
        const ratio = Math.min(maxWidth / logo.width, maxHeight / logo.height);
        const width = logo.width * ratio;
        const height = logo.height * ratio;
        if (logo.mimeType === 'image/svg+xml') {
            return this.renderInlineSvgLogo(logo.data, x, y, width, height, logo.width, logo.height);
        }
        const dataUri = logo.mimeType ? `data:${logo.mimeType};base64,${logo.data}` : this.getThumbnailDataUri(logo.data);
        return `<image href="${dataUri}" x="${x}" y="${y}" width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet"/>`;
    }
    static decodeBase64Utf8(base64Data) {
        const binary = atob(base64Data);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder('utf-8').decode(bytes);
    }
    static renderInlineSvgLogo(base64Data, x, y, width, height, sourceWidth, sourceHeight) {
        const svgContent = this.decodeBase64Utf8(base64Data);
        const innerMatch = svgContent.match(/<svg[^>]*>([\s\S]*)<\/svg>/i);
        if (!innerMatch) {
            return '';
        }
        const scaleX = width / sourceWidth;
        const scaleY = height / sourceHeight;
        return `<g transform="translate(${x},${y}) scale(${scaleX},${scaleY})">${innerMatch[1]}</g>`;
    }
    static renderTitle(submittal) {
        const code = SubmittalCommonUtils.getSubmittalCode(submittal);
        return `<text x="${this.MARGIN}" y="168" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">${this.escapeXml(`Submittal #${code}`)}</text>
  <text x="${this.MARGIN}" y="202" font-family="${this.FONT}" font-size="${this.TITLE_SIZE}" font-weight="bold" fill="#000000">${this.escapeXml(submittal.title ?? '')}</text>`;
    }
    /** User-facing submittal number; falls back to legacy `number` field. */
    static getSubmittalCode(submittal) {
        return submittal?.code || submittal?.number || submittal?._id || '';
    }
    static renderFieldColumn(fields, x, startY, width) {
        let y = startY;
        const parts = [];
        for (const field of fields) {
            if (field.multiline) {
                const block = this.renderMultilineField(field.label, field.value, x, y, width);
                parts.push(block.svg);
                y = block.endY + 6;
            }
            else {
                parts.push(this.renderFieldRow(field.label, field.value, x, y, width));
                y += this.LINE_HEIGHT;
            }
        }
        return { svg: parts.join('\n  '), endY: y };
    }
    static renderFieldRow(label, value, x, y, width) {
        const lines = this.wrapText(value, width - this.LABEL_WIDTH, this.BODY_SIZE);
        const firstValue = lines[0] ?? '';
        const continuation = lines
            .slice(1)
            .map((line, index) => `<tspan x="${x + this.LABEL_WIDTH}" dy="${index === 0 ? this.LINE_HEIGHT : this.LINE_HEIGHT}">${this.escapeXml(line)}</tspan>`)
            .join('');
        return `<text x="${x}" y="${y}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">
    <tspan font-weight="bold">${this.escapeXml(label)}</tspan>
    <tspan dx="4" font-weight="normal">${this.escapeXml(firstValue)}</tspan>${continuation}
  </text>`;
    }
    static renderMultilineField(label, value, x, y, width) {
        const lines = this.wrapText(value, width - this.LABEL_WIDTH, this.BODY_SIZE);
        if (lines.length === 0) {
            return {
                svg: `<text x="${x}" y="${y}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">
    <tspan font-weight="bold">${this.escapeXml(label)}</tspan>
  </text>`,
                endY: y,
            };
        }
        const tspans = lines
            .map((line, index) => {
            const lineX = index === 0 ? x + this.LABEL_WIDTH + 4 : x + this.LABEL_WIDTH;
            const prefix = index === 0
                ? `<tspan font-weight="bold">${this.escapeXml(label)}</tspan><tspan dx="4" font-weight="normal">${this.escapeXml(line)}</tspan>`
                : `<tspan x="${lineX}" dy="${this.LINE_HEIGHT}" font-weight="normal">${this.escapeXml(line)}</tspan>`;
            return prefix;
        })
            .join('');
        return {
            svg: `<text x="${x}" y="${y}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">${tspans}</text>`,
            endY: y + this.LINE_HEIGHT * Math.max(lines.length - 1, 0),
        };
    }
    static renderWorkflowSection(submittal, revision, people, feed, x, y, width, calendar) {
        const titleY = y;
        const tableY = titleY + 16;
        const columnWidths = [0.18, 0.14, 0.14, 0.14, 0.18, 0.22].map((ratio) => Math.round(width * ratio));
        const totalWidth = columnWidths.reduce((sum, col) => sum + col, 0);
        const adjustedColumnWidths = [...columnWidths];
        adjustedColumnWidths[adjustedColumnWidths.length - 1] += width - totalWidth;
        const headers = ['Action', 'By', 'Date', 'Due Date', 'Returned Date', 'Comment'];
        const rows = this.getWorkflowRows(submittal, revision, people, feed, calendar);
        const bodyHeight = rows.length * this.TABLE_ROW_HEIGHT;
        const tableHeight = this.TABLE_HEADER_HEIGHT + bodyHeight;
        let cursorX = x;
        const separators = [];
        for (let i = 0; i < adjustedColumnWidths.length - 1; i++) {
            cursorX += adjustedColumnWidths[i];
            separators.push(`<line x1="${cursorX}" y1="${tableY}" x2="${cursorX}" y2="${tableY + tableHeight}" stroke="#d0d0d0" stroke-width="1"/>`);
        }
        const horizontalLines = [
            `<line x1="${x}" y1="${tableY + this.TABLE_HEADER_HEIGHT}" x2="${x + width}" y2="${tableY + this.TABLE_HEADER_HEIGHT}" stroke="#d0d0d0" stroke-width="1"/>`,
        ];
        for (let i = 1; i < rows.length; i++) {
            const lineY = tableY + this.TABLE_HEADER_HEIGHT + i * this.TABLE_ROW_HEIGHT;
            horizontalLines.push(`<line x1="${x}" y1="${lineY}" x2="${x + width}" y2="${lineY}" stroke="#ebebeb" stroke-width="1"/>`);
        }
        const headerTexts = headers
            .map((header, index) => {
            const cellStart = x + adjustedColumnWidths.slice(0, index).reduce((sum, col) => sum + col, 0);
            return `<text x="${cellStart + 6}" y="${tableY + 16}" font-family="${this.FONT}" font-size="10.5" font-weight="bold" fill="#333333">${this.escapeXml(header)}</text>`;
        })
            .join('\n  ');
        const rowTexts = rows
            .map((row, rowIndex) => {
            const rowY = tableY + this.TABLE_HEADER_HEIGHT + rowIndex * this.TABLE_ROW_HEIGHT + 16;
            return row
                .map((value, colIndex) => {
                const cellStart = x + adjustedColumnWidths.slice(0, colIndex).reduce((sum, col) => sum + col, 0);
                const maxChars = Math.max(8, Math.floor((adjustedColumnWidths[colIndex] - 12) / 5.5));
                return `<text x="${cellStart + 6}" y="${rowY}" font-family="${this.FONT}" font-size="10.5" fill="#222222">${this.escapeXml(this.truncateText(value, maxChars))}</text>`;
            })
                .join('\n  ');
        })
            .join('\n  ');
        return {
            svg: `<line x1="${x}" y1="${titleY + 20}" x2="${x + width}" y2="${titleY + 20}" stroke="#d8d8d8" stroke-width="1"/>
  <text x="${x}" y="${titleY}" font-family="${this.FONT}" font-size="19" font-weight="bold" fill="#111111">Submittal Workflow</text>
  <rect x="${x}" y="${tableY}" width="${width}" height="${tableHeight}" fill="#ffffff" stroke="#d0d0d0" stroke-width="1"/>
  <rect x="${x}" y="${tableY}" width="${width}" height="${this.TABLE_HEADER_HEIGHT}" fill="#f0f0f0"/>
  ${headerTexts}
  ${rowTexts}
  ${separators.join('\n  ')}
  ${horizontalLines.join('\n  ')}`,
            endY: tableY + tableHeight,
        };
    }
    static renderAttachmentsSection(files, x, y, width) {
        const titleY = y;
        const sectionY = titleY + 12;
        if (!files.length) {
            return {
                svg: `<line x1="${x}" y1="${titleY + 20}" x2="${x + width}" y2="${titleY + 20}" stroke="#d8d8d8" stroke-width="1"/>
  <text x="${x}" y="${titleY}" font-family="${this.FONT}" font-size="15" font-weight="bold" fill="#111111">Attachments</text>
  <text x="${x + 8}" y="${sectionY + 22}" font-family="${this.FONT}" font-size="11" fill="#5a5a5a">No attachments included in this revision.</text>`,
                endY: sectionY + 30,
            };
        }
        const visibleFiles = files.filter((file) => file.type != FileType.PHOTO).slice(0, this.MAX_ATTACHMENT_ROWS);
        const hiddenCount = files.length - visibleFiles.length;
        const rowParts = visibleFiles.map((file, index) => {
            const rowY = sectionY + index * this.ATTACHMENT_ROW_HEIGHT;
            const thumbX = x + 8;
            const thumbY = rowY + 8;
            const thumbSvg = this.renderAttachmentThumbnail(file, thumbX, thumbY);
            const name = this.truncateText(file.name || 'Untitled file', 76);
            const sizeLabel = typeof file.num_bytes === 'number' && file.num_bytes > 0
                ? `${Math.max(1, Math.round(file.num_bytes / 1024))} KB`
                : '';
            const meta = [file.type, sizeLabel].filter(Boolean).join(' • ');
            return `<rect x="${x}" y="${rowY}" width="${width}" height="${this.ATTACHMENT_ROW_HEIGHT}" fill="#ffffff" stroke="#e3e3e3" stroke-width="1"/>
  ${thumbSvg}
  <text x="${thumbX + this.ATTACHMENT_THUMB_SIZE + 10}" y="${rowY + 24}" font-family="${this.FONT}" font-size="11.5" font-weight="bold" fill="#222222">${this.escapeXml(name)}</text>
  <text x="${thumbX + this.ATTACHMENT_THUMB_SIZE + 10}" y="${rowY + 40}" font-family="${this.FONT}" font-size="10" fill="#606060">${this.escapeXml(meta)}</text>`;
        });
        const hiddenFilesLabel = hiddenCount > 0
            ? `<text x="${x + 8}" y="${sectionY + visibleFiles.length * this.ATTACHMENT_ROW_HEIGHT + 18}" font-family="${this.FONT}" font-size="10.5" fill="#5a5a5a">+${hiddenCount} more attachment${hiddenCount === 1 ? '' : 's'}</text>`
            : '';
        const sectionHeight = visibleFiles.length * this.ATTACHMENT_ROW_HEIGHT + (hiddenCount > 0 ? 24 : 0);
        return {
            svg: `<line x1="${x}" y1="${titleY + 20}" x2="${x + width}" y2="${titleY + 20}" stroke="#d8d8d8" stroke-width="1"/>
  <text x="${x}" y="${titleY}" font-family="${this.FONT}" font-size="15" font-weight="bold" fill="#111111">Attachments</text>
  ${rowParts.join('\n  ')}
  ${hiddenFilesLabel}`,
            endY: sectionY + sectionHeight,
        };
    }
    static getWorkflowRows(submittal, revision, people, feed, calendar) {
        const revisionNum = revision?.num;
        const statusItemsAsc = this.filterStatusFeedItems(feed, revision)
            .filter((item) => !item.deleted_by &&
            !item.deleted_date &&
            (revisionNum === undefined || revisionNum === null || item.revision === revisionNum))
            .sort(FeedUtils.sortOldestFirst);
        return statusItemsAsc.map((item, index) => {
            const stateId = item.sub_id ?? null;
            const stateIndex = revision.states.findIndex((state) => state.id === stateId);
            const state = stateIndex >= 0 ? revision.states[stateIndex] : null;
            const doneDate = DateUtils.fromISO(item.date);
            const dueDate = item.data?.dueDate
                ? DateUtils.fromISO(item.data.dueDate)
                : this.getStateDueDate(state, doneDate, calendar);
            let returnedDate = '';
            if (index > 0) {
                const prevItem = statusItemsAsc[index - 1];
                const previousStateIndex = revision.states.findIndex((s) => s.id === prevItem.sub_id);
                // Previous status was further ahead in the workflow → this entry is a return
                if (previousStateIndex > stateIndex) {
                    returnedDate = DateUtils.formatIso(prevItem.date) || '';
                }
            }
            const action = state?.pastTense ||
                item.data?.pastTense ||
                (typeof item.data?.action === 'string' ? item.data.action : '') ||
                'Completed';
            const person = this.findPerson(people, item.user_id);
            const personName = person ? this.getFullName(person) : 'Unknown User';
            const completeDate = DateUtils.formatIso(item.date) || '';
            return [action, personName, completeDate, DateUtils.format(dueDate), returnedDate, item.text || ''];
        });
    }
    static getFeedPageSvg(submittal, people, project, company, feed, files = []) {
        const x = this.MARGIN;
        const width = this.PAGE_WIDTH - this.MARGIN * 2;
        const feedTitleY = 266;
        const tableY = feedTitleY + 12;
        const visibleFeed = (feed || [])
            .filter((item) => !item.deleted_by && !item.deleted_date)
            .sort((a, b) => Date.parse(b.date || '') - Date.parse(a.date || ''));
        const commentEntries = this.getRenderableCommentEntries(visibleFeed.filter((item) => item.sub === FeedItemSubType.COMMENT), people);
        const photoEntries = this.getRenderablePhotoEntries(visibleFeed.filter((item) => item.sub === FeedItemSubType.PHOTO), people, files);
        let contentY = tableY + 10;
        const rowParts = [];
        for (let i = 0; i < commentEntries.length; i++) {
            const entry = commentEntries[i];
            const metaLines = this.wrapText(entry.meta, width - 20, this.FEED_META_SIZE);
            const textLines = this.wrapText(entry.text, width - 20, this.FEED_TEXT_SIZE).slice(0, 4);
            const metaHeight = Math.max(1, metaLines.length) * 11;
            const textHeight = Math.max(1, textLines.length) * 16;
            const rowHeight = Math.max(this.FEED_ROW_MIN_HEIGHT, 8 + metaHeight + textHeight + 8);
            const metaSvg = metaLines
                .map((line, index) => {
                const lineY = contentY + 11 + index * 11;
                return `<text x="${x + 8}" y="${lineY}" font-family="${this.FONT}" font-size="${this.FEED_META_SIZE}" fill="#4b4b4b">${this.escapeXml(line)}</text>`;
            })
                .join('\n  ');
            const textBaseY = contentY + 11 + metaHeight + 4;
            const textSvg = textLines
                .map((line, index) => {
                const lineY = textBaseY + index * 16;
                return `<text x="${x + 8}" y="${lineY}" font-family="${this.FONT}" font-size="${this.FEED_TEXT_SIZE}" fill="#222222">${this.escapeXml(line)}</text>`;
            })
                .join('\n  ');
            const separator = i < commentEntries.length - 1
                ? `<line x1="${x}" y1="${contentY + rowHeight}" x2="${x + width}" y2="${contentY + rowHeight}" stroke="#b8b8b8" stroke-width="1"/>`
                : '';
            rowParts.push(`${metaSvg}
  ${textSvg}
  ${separator}`);
            contentY += rowHeight;
        }
        const tableHeight = Math.max(this.FEED_ROW_MIN_HEIGHT, contentY - tableY);
        const commentsSvg = `<rect x="${x}" y="${tableY}" width="${width}" height="${tableHeight}" fill="#ffffff" stroke="#6f6f6f" stroke-width="1"/>
  ${rowParts.join('\n  ')}`;
        const photosStartY = tableY + tableHeight + this.SECTION_GAP;
        const photosSvg = this.renderFeedPhotosHorizontal(photoEntries, x, photosStartY, width);
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${this.PAGE_WIDTH}" height="${this.PAGE_HEIGHT}" viewBox="0 0 ${this.PAGE_WIDTH} ${this.PAGE_HEIGHT}" style="display:block">
  <rect width="100%" height="100%" fill="#ffffff"/>
  <rect x="1" y="1" width="${this.PAGE_WIDTH - 2}" height="${this.PAGE_HEIGHT - 2}" fill="none" stroke="#222222" stroke-width="1"/>
  ${this.renderHeader(company, project)}
  ${this.renderTitle(submittal)}
  <text x="${x}" y="${feedTitleY}" font-family="${this.FONT}" font-size="26" font-weight="bold" fill="#111111">Accepted Answers</text>
  ${commentsSvg}
  ${photosSvg}
  ${this.renderFooter(company, 2, 2)}
</svg>`;
    }
    static getRenderableCommentEntries(feed, people) {
        if (!feed.length) {
            return [{ meta: 'No comments', text: 'No accepted comment answers for this submittal.' }];
        }
        return feed.map((item) => {
            const person = this.findPerson(people, item.user_id);
            const personName = person ? this.getFullName(person) : 'Unknown User';
            const company = person?.company ? `, ${person.company}` : '';
            const date = item.date ? DateUtils.formatIso(item.date) : '';
            const meta = [personName + company, date].filter(Boolean).join('  •  ');
            return { meta, text: item.text || '' };
        });
    }
    static getRenderablePhotoEntries(feed, people, files) {
        return feed
            .map((item) => {
            if (!item.sub_id)
                return null;
            const file = files.find((f) => f._id === item.sub_id);
            if (!file?.thumb || !file.thumb_width || !file.thumb_height)
                return null;
            const creator = this.findPerson(people, file.created_by || '');
            const creatorName = creator ? this.getFullName(creator) : 'Unknown User';
            const createdDate = file.created_date ? DateUtils.formatIso(file.created_date) : '';
            return {
                thumb: file.thumb,
                width: file.thumb_width,
                height: file.thumb_height,
                creatorName,
                createdDate,
            };
        })
            .filter((entry) => !!entry);
    }
    /** Horizontal photo strip after the comments table. */
    static renderFeedPhotosHorizontal(photos, x, startY, maxWidth) {
        if (!photos.length) {
            return '';
        }
        const gap = 12;
        const captionLineHeight = 14;
        const captionBlockHeight = captionLineHeight * 2 + 6;
        let cursorX = x;
        let rowY = startY;
        let rowMaxHeight = 0;
        const parts = [];
        for (const photo of photos) {
            if (cursorX > x && cursorX + photo.width > x + maxWidth) {
                cursorX = x;
                rowY += rowMaxHeight + gap;
                rowMaxHeight = 0;
            }
            const dataUri = this.getThumbnailDataUri(photo.thumb);
            const captionName = this.truncateText(photo.creatorName, 28);
            const captionDate = photo.createdDate;
            parts.push(`<image href="${dataUri}" x="${cursorX}" y="${rowY}" width="${photo.width}" height="${photo.height}" preserveAspectRatio="xMidYMid meet"/>
  <text x="${cursorX}" y="${rowY + photo.height + captionLineHeight}" font-family="${this.FONT}" font-size="10" fill="#222222">${this.escapeXml(captionName)}</text>
  <text x="${cursorX}" y="${rowY + photo.height + captionLineHeight * 2}" font-family="${this.FONT}" font-size="10" fill="#606060">${this.escapeXml(captionDate)}</text>`);
            cursorX += photo.width + gap;
            rowMaxHeight = Math.max(rowMaxHeight, photo.height + captionBlockHeight);
        }
        return parts.join('\n  ');
    }
    static renderFooter(company, pageNumber, totalPages) {
        const footerY = this.PAGE_HEIGHT - 26;
        const printedOn = this.getPrintedOnLabel();
        return `<line x1="${this.MARGIN}" y1="${footerY - 30}" x2="${this.PAGE_WIDTH - this.MARGIN}" y2="${footerY - 30}" stroke="#e0e0e0" stroke-width="1"/>
  <text x="${this.MARGIN}" y="${footerY}" font-family="${this.FONT}" font-size="11" fill="#222222">${this.escapeXml(company.name)}</text>
  <text x="${this.PAGE_WIDTH / 2}" y="${footerY}" text-anchor="middle" font-family="${this.FONT}" font-size="11" fill="#222222">Page ${pageNumber} of ${totalPages}</text>
  <text x="${this.PAGE_WIDTH - this.MARGIN}" y="${footerY}" text-anchor="end" font-family="${this.FONT}" font-size="11" fill="#222222">${this.escapeXml(printedOn)}</text>`;
    }
    static getPrintedOnLabel() {
        const now = new Date();
        const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        const timezone = now.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
        return `Printed On: ${date} ${time} ${timezone}`.trim();
    }
    static truncateText(value, maxChars) {
        if (!value) {
            return '';
        }
        if (value.length <= maxChars) {
            return value;
        }
        return `${value.slice(0, Math.max(1, maxChars - 1)).trimEnd()}...`;
    }
    static renderAttachmentThumbnail(file, x, y) {
        const thumb = file.thumb;
        const border = `<rect x="${x}" y="${y}" width="${this.ATTACHMENT_THUMB_SIZE}" height="${this.ATTACHMENT_THUMB_SIZE}" rx="2" ry="2" fill="#f6f6f6" stroke="#d0d0d0" stroke-width="1"/>`;
        if (!thumb) {
            const fallbackLabel = this.getFileExtensionLabel(file.name);
            return `${border}
  <text x="${x + this.ATTACHMENT_THUMB_SIZE / 2}" y="${y + this.ATTACHMENT_THUMB_SIZE / 2 + 4}" text-anchor="middle" font-family="${this.FONT}" font-size="8" fill="#7a7a7a">${this.escapeXml(fallbackLabel)}</text>`;
        }
        const dataUri = this.getThumbnailDataUri(thumb);
        return `${border}
  <image href="${dataUri}" x="${x + 1}" y="${y + 1}" width="${this.ATTACHMENT_THUMB_SIZE - 2}" height="${this.ATTACHMENT_THUMB_SIZE - 2}" preserveAspectRatio="xMidYMid slice"/>`;
    }
    static getFileExtensionLabel(fileName) {
        if (!fileName) {
            return 'FILE';
        }
        const lastDot = fileName.lastIndexOf('.');
        if (lastDot < 0 || lastDot === fileName.length - 1) {
            return 'FILE';
        }
        return fileName
            .substring(lastDot + 1)
            .toUpperCase()
            .slice(0, 4);
    }
    static getThumbnailDataUri(thumb) {
        if (thumb.startsWith('data:')) {
            return thumb;
        }
        if (thumb.startsWith('/9j/')) {
            return `data:image/jpeg;base64,${thumb}`;
        }
        if (thumb.startsWith('iVBOR')) {
            return `data:image/png;base64,${thumb}`;
        }
        if (thumb.startsWith('R0lGOD')) {
            return `data:image/gif;base64,${thumb}`;
        }
        if (thumb.startsWith('UklGR')) {
            return `data:image/webp;base64,${thumb}`;
        }
        if (thumb.startsWith('PD94bWwg') || thumb.startsWith('PHN2Zy')) {
            return `data:image/svg+xml;base64,${thumb}`;
        }
        return `data:image/jpeg;base64,${thumb}`;
    }
    static formatPersonWithCompany(people, personId) {
        const person = this.findPerson(people, personId);
        if (!person) {
            return '';
        }
        const name = this.getFullName(person);
        return person.company ? `${name} (${person.company})` : name;
    }
    static formatPersonCompany(people, personId) {
        const person = this.findPerson(people, personId);
        return person?.company ?? '';
    }
    static formatPeopleList(people, personIds) {
        return (personIds ?? [])
            .map((personId) => this.formatPersonWithCompany(people, personId))
            .filter(Boolean)
            .join(', ');
    }
    static wrapText(text, maxWidth, fontSize) {
        if (!text) {
            return [];
        }
        const charsPerLine = Math.max(12, Math.floor(maxWidth / (fontSize * 0.55)));
        const paragraphs = text.split(/\r?\n/);
        const lines = [];
        for (const paragraph of paragraphs) {
            const words = paragraph.split(/\s+/).filter(Boolean);
            if (words.length === 0) {
                lines.push('');
                continue;
            }
            let current = words[0];
            for (let i = 1; i < words.length; i++) {
                const candidate = `${current} ${words[i]}`;
                if (candidate.length <= charsPerLine) {
                    current = candidate;
                }
                else {
                    lines.push(current);
                    current = words[i];
                }
            }
            lines.push(current);
        }
        return lines;
    }
    static escapeXml(text) {
        if (!text) {
            return '';
        }
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    static findPerson(people, personId) {
        return people.find((person) => person._id === personId) ?? null;
    }
    static getFullName(person) {
        return `${person.first_name} ${person.last_name}`;
    }
}
